/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/prop-types */
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  IconButton,
  Box,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import usePostJsonData from "@/hooks/postHooks/usePostJsonData";
import useAuthenticatedQuery from "@/hooks/useAuthenticatedQuery";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

const ChecklistFormDialog = ({ open, onClose, checklist, documentId }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({});
  const [readonlyFields, setReadonlyFields] = useState({});
  const { mutate: postData, isLoading } = usePostJsonData();

  const queryKey = ["checklist-answers", documentId, checklist?.id];
  const { data: checklistWithAnswers, isLoading: isFetchingAnswers } =
    useAuthenticatedQuery(
      queryKey,
      `/api/v1/workpermits/${documentId}/checklists/${checklist?.id}/answers`,
      {
        enabled: open && !!checklist?.id && !!documentId,
      }
    );

  const formatDate = (isoString) => {
    if (!isoString) return t("checklistFormDialog.unknownDate");
    const date = new Date(isoString);
    return date.toLocaleString(undefined, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  useEffect(() => {
    if (checklistWithAnswers && Array.isArray(checklistWithAnswers.items)) {
      const initialData = {};
      const readonlyMap = {};

      checklistWithAnswers.items.forEach((item) => {
        if (!item.is_section && item.answer !== null) {
          initialData[item.id] = item.answer;
          readonlyMap[item.id] = {
            name: item.answered_by
              ? `${item.answered_by.first_name || t("checklistFormDialog.unknownUser")} ${item.answered_by.last_name || t("checklistFormDialog.unknownUser")}`
              : t("checklistFormDialog.unknownUser"),
            date: item.created_at
              ? formatDate(item.created_at)
              : t("checklistFormDialog.unknownDate"),
          };
        }
      });

      setFormData(initialData);
      setReadonlyFields(readonlyMap);
    }
  }, [checklistWithAnswers]);

  const handleChange = (id, value) => {
    if (readonlyFields[id]) return;
    setFormData((prev) => ({
      ...prev,
      [id]: value.toUpperCase(),
    }));
  };

  const handleSubmit = () => {
    const payload = checklist.items
      .filter((item) => !item.is_section && !readonlyFields[item.id])
      .map((item) => ({
        checklist_item_id: item.id,
        answer: (formData[item.id] || "").toUpperCase(),
      }))
      .filter((entry) => entry.answer.trim() !== "");

    postData(
      {
        url: `/api/v1/workpermits/${documentId}/checklists/${checklist.id}/answers`,
        data: payload,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(queryKey);
          onClose();
        },
      }
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 2,
          bgcolor: theme.palette.checklist.surface.elevated,
        },
      }}
      aria-labelledby="checklist-form-dialog-title"
    >
      <DialogTitle
        id="checklist-form-dialog-title"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 2,
          px: 3,
          bgcolor: theme.palette.checklist.surface.subtle,
          borderBottom: 1,
          borderColor: theme.palette.checklist.border.subtle,
        }}
      >
        <Typography component="span" variant="h5" fontWeight="bold">
          {checklist?.name || t("checklistFormDialog.unknownName")}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            bgcolor: theme.palette.checklist.interactive.hover,
            "&:hover": {
              bgcolor: theme.palette.checklist.interactive.selected,
            },
          }}
          aria-label={t("checklistFormDialog.closeDialogAria")}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          bgcolor: theme.palette.checklist.surface.primary,
        }}
      >
        {checklist?.items.map((item) =>
          item.is_section ? (
            <Typography
              key={item.id}
              variant="h6"
              sx={{
                mt: 3,
                mb: 1,
                fontWeight: "bold",
                color: theme.palette.checklist.section.text,
                p: 2,
                bgcolor: theme.palette.checklist.section.background,
                borderRadius: 1,
                border: 1,
                borderColor: theme.palette.checklist.section.border,
              }}
            >
              {item.text || t("checklistFormDialog.unknownSection")}
            </Typography>
          ) : (
            <Box key={item.id} sx={{ mb: 3 }}>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  color: theme.palette.checklist.question.text,
                  fontWeight: 500,
                }}
              >
                {item.text || t("checklistFormDialog.unknownItemText")}
              </Typography>
              <Typography
                variant="body2"
                sx={{ mb: 1, color: theme.palette.text.secondary }}
              >
                {item.description ||
                  t("checklistFormDialog.unknownDescription")}
              </Typography>
              <ToggleButtonGroup
                exclusive
                value={formData[item.id] || null}
                onChange={(e, newValue) => handleChange(item.id, newValue)}
                disabled={!!readonlyFields[item.id]}
                sx={{ gap: 1, flexWrap: "wrap" }}
                aria-label={t("checklistFormDialog.answerAria", {
                  text: item.text || t("checklistFormDialog.unknownItemText"),
                })}
              >
                {["YES", "NO", "NA"].map((value) => (
                  <ToggleButton
                    key={value}
                    value={value}
                    sx={{
                      px: 2.5,
                      py: 1,
                      borderRadius: 4,
                      fontWeight: 500,
                      textTransform: "none",
                      minWidth: 80,
                      transition: "all 0.3s ease",
                      border: `1px solid ${theme.palette.checklist.border.subtle}`,
                      bgcolor:
                        formData[item.id] === value
                          ? value === "YES"
                            ? theme.palette.success.dark
                            : value === "NO"
                              ? theme.palette.error.dark
                              : theme.palette.warning.dark
                          : theme.palette.checklist.surface.elevated,
                      color:
                        formData[item.id] === value
                          ? theme.palette.common.white
                          : theme.palette.text.primary,
                      "&:hover": {
                        bgcolor:
                          value === "YES"
                            ? theme.palette.success.main
                            : value === "NO"
                              ? theme.palette.error.main
                              : theme.palette.warning.main,
                        color: theme.palette.common.white,
                      },
                      "&.Mui-selected": {
                        bgcolor:
                          value === "YES"
                            ? theme.palette.success.dark
                            : value === "NO"
                              ? theme.palette.error.dark
                              : theme.palette.warning.dark,
                        color: theme.palette.common.white,
                        boxShadow: theme.shadows[2],
                        transform: "scale(1.05)",
                        borderColor: theme.palette.checklist.border.focus,
                        "&:hover": {
                          bgcolor:
                            value === "YES"
                              ? theme.palette.success.main
                              : value === "NO"
                                ? theme.palette.error.main
                                : theme.palette.warning.main,
                        },
                      },
                      "&.Mui-disabled": {
                        opacity: 0.5,
                        color: theme.palette.checklist.interactive.disabled,
                      },
                    }}
                    aria-label={t("checklistFormDialog.toggleButtonAria", {
                      value,
                    })}
                  >
                    {t(`checklistFormDialog.toggleButton${value}`)}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              {readonlyFields[item.id] && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: "block" }}
                >
                  {t("checklistFormDialog.answeredByLabel", {
                    name: readonlyFields[item.id].name,
                    date: readonlyFields[item.id].date,
                  })}
                </Typography>
              )}
            </Box>
          )
        )}
      </DialogContent>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 2,
          py: 2,
          px: 3,
          bgcolor: theme.palette.checklist.surface.subtle,
          borderTop: 1,
          borderColor: theme.palette.checklist.border.subtle,
        }}
      >
        <Button
          onClick={onClose}
          disabled={isLoading}
          variant="outlined"
          sx={{
            minWidth: 100,
            borderRadius: 2,
            textTransform: "none",
          }}
          aria-label={t("checklistFormDialog.closeButtonAria")}
        >
          {t("checklistFormDialog.closeButton")}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isLoading || isFetchingAnswers}
          sx={{
            minWidth: 100,
            borderRadius: 2,
            textTransform: "none",
            fontWeight: "bold",
          }}
          aria-label={t("checklistFormDialog.saveButtonAria")}
        >
          {isLoading
            ? t("checklistFormDialog.submittingText")
            : t("checklistFormDialog.saveButton")}
        </Button>
      </Box>
    </Dialog>
  );
};

export default ChecklistFormDialog;
